import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { combos as combosApi, products as prodApi, type Combo, type Product } from '../lib/api.js'
import {
  Button,
  Input,
  Select,
  Modal,
  Badge,
  Empty,
  Spinner,
  toast,
} from '../components/ui/index.js'
import { formatCents, parseCents } from '../lib/utils.js'
import { useI18n } from '../lib/i18n.js'
import { config as configApi } from '../lib/api.js'
import { intlLocaleFor } from '@self-restaurant/shared'

/** Nome do produto no idioma do painel (exato → base → primeira tradução). */
function productNameFor(
  p: { id: string; translations: Array<{ locale: string; name: string | null }> } | undefined,
  lang: string,
): string {
  if (!p) return '—'
  const base = lang.split('-')[0] ?? lang
  return (
    p.translations.find((tr) => tr.locale === lang)?.name ??
    p.translations.find((tr) => tr.locale.startsWith(base))?.name ??
    p.translations.find((tr) => tr.name)?.name ??
    p.id
  )
}

export function Combos() {
  const { t, lang } = useI18n()
  const qc = useQueryClient()
  // #428: moeda e país do tenant — o desconto fixo é mostrado na moeda certa
  const { data: cfg } = useQuery({ queryKey: ['config'], queryFn: () => configApi.get() })
  const currency = cfg?.currency ?? 'BRL'
  const priceLocale = intlLocaleFor(lang, cfg?.country_code)
  const [modal, setModal] = useState<{ open: boolean; combo?: Combo }>({ open: false })

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['combos', lang],
    queryFn: () => combosApi.list(lang),
  })
  const { data: allProducts = [] } = useQuery({
    queryKey: ['products'],
    queryFn: () => prodApi.list(),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => combosApi.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['combos'] })
      toast.success(t('admin.combos.deactivated_toast'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.nav.combos')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('admin.combos.subtitle')}</p>
        </div>
        <Button
          size="sm"
          onClick={() => setModal({ open: true })}
          disabled={allProducts.filter((p) => p.is_active).length < 2}
        >
          {t('admin.combos.new')}
        </Button>
      </div>

      {list.length === 0 ? (
        <Empty
          title={t('admin.combos.empty')}
          description={t('admin.combos.empty_hint')}
          action={
            <Button size="sm" onClick={() => setModal({ open: true })}>
              {t('admin.combos.create')}
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((combo) => (
            <ComboCard
              key={combo.id}
              combo={combo}
              products={allProducts}
              currency={currency}
              priceLocale={priceLocale}
              onEdit={() => setModal({ open: true, combo })}
              onDelete={() => deleteMut.mutate(combo.id)}
            />
          ))}
        </div>
      )}

      <ComboModal
        {...modal}
        onClose={() => setModal({ open: false })}
        products={allProducts.filter((p) => p.is_active)}
        qc={qc}
      />
    </div>
  )
}

function ComboCard({
  combo,
  products,
  currency = 'BRL',
  priceLocale = '',
  onEdit,
  onDelete,
}: {
  combo: Combo
  products: Product[]
  /** #428: moeda do país do tenant e locale de formatação Intl */
  currency?: string
  priceLocale?: string
  onEdit: () => void
  onDelete: () => void
}) {
  const { t, lang } = useI18n()
  const itemNames = combo.items.map((item) => {
    const p = products.find((x) => x.id === item.product_id)
    const name = 'product_name' in item ? (item.product_name as string) : productNameFor(p, lang)
    return `${item.quantity}× ${name}`
  })

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <p className="font-semibold text-gray-900">{combo.name}</p>
        <Badge color={combo.is_active ? 'green' : 'gray'}>
          {combo.is_active ? t('admin.common.active') : t('admin.common.inactive')}
        </Badge>
      </div>
      <div className="text-2xl font-bold text-indigo-600">
        {combo.discount_type === 'percent'
          ? `-${combo.discount_value}%`
          : `-${formatCents(combo.discount_value, currency, priceLocale)}`}
      </div>
      <ul className="space-y-1">
        {itemNames.map((n, i) => (
          <li key={i} className="text-sm text-gray-600 flex items-center gap-1">
            <span className="text-gray-300">•</span> {n}
          </li>
        ))}
      </ul>
      <div className="flex gap-2 mt-auto pt-3 border-t border-gray-100">
        <Button variant="ghost" size="sm" onClick={onEdit}>
          {t('admin.common.edit')}
        </Button>
        <Button variant="danger" size="sm" onClick={onDelete}>
          {t('admin.combos.deactivate')}
        </Button>
      </div>
    </div>
  )
}

function ComboModal({
  open,
  combo,
  products,
  onClose,
  qc,
}: {
  open: boolean
  combo?: Combo
  products: Product[]
  onClose: () => void
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t, lang } = useI18n()
  const [name, setName] = useState(combo?.name ?? '')
  const [discType, setDiscType] = useState<'fixed' | 'percent'>(combo?.discount_type ?? 'percent')
  const [discValue, setDiscValue] = useState(
    combo
      ? discType === 'percent'
        ? String(combo.discount_value)
        : String(combo.discount_value / 100).replace('.', ',')
      : '',
  )
  const [items, setItems] = useState<{ product_id: string; quantity: number }[]>(
    combo?.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })) ?? [],
  )

  const [prevOpen, setPrevOpen] = useState(false)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setName(combo?.name ?? '')
      setDiscType(combo?.discount_type ?? 'percent')
      setDiscValue(combo ? String(combo.discount_value) : '')
      setItems(combo?.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })) ?? [])
    }
  }

  const mut = useMutation({
    mutationFn: () => {
      const value = discType === 'fixed' ? parseCents(discValue) : parseInt(discValue, 10)
      return combo
        ? combosApi.update(combo.id, {
            name,
            discount_type: discType,
            discount_value: value,
            items,
          })
        : combosApi.create({ name, discount_type: discType, discount_value: value, items })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['combos'] })
      toast.success(combo ? t('admin.combos.updated_toast') : t('admin.combos.created_toast'))
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function addItem(productId: string) {
    if (items.some((i) => i.product_id === productId)) return
    setItems((prev) => [...prev, { product_id: productId, quantity: 1 }])
  }

  function removeItem(productId: string) {
    setItems((prev) => prev.filter((i) => i.product_id !== productId))
  }

  const availableProducts = products.filter((p) => !items.some((i) => i.product_id === p.id))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={combo ? t('admin.combos.edit_title') : t('admin.combos.new_title')}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button
            size="sm"
            loading={mut.isPending}
            onClick={() => mut.mutate()}
            disabled={items.length < 2}
          >
            {combo ? t('admin.common.save') : t('admin.combos.create_short')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label={t('admin.combos.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />

        <div className="grid grid-cols-2 gap-3">
          <Select
            label={t('admin.combos.discount_type')}
            value={discType}
            onChange={(e) => setDiscType(e.target.value as 'fixed' | 'percent')}
            options={[
              { value: 'percent', label: t('admin.combos.discount_percent') },
              { value: 'fixed', label: t('admin.combos.discount_fixed') },
            ]}
          />
          <Input
            label={
              discType === 'percent'
                ? t('admin.combos.discount_value_percent')
                : t('admin.combos.discount_value_fixed')
            }
            value={discValue}
            onChange={(e) => setDiscValue(e.target.value)}
            placeholder={discType === 'percent' ? '10' : '5,00'}
          />
        </div>

        {/* Itens */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">{t('admin.combos.items')}</p>
          <div className="space-y-2 mb-3">
            {items.map((item) => {
              const p = products.find((x) => x.id === item.product_id)
              const pName = productNameFor(p, lang)
              return (
                <div
                  key={item.product_id}
                  className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2"
                >
                  <span className="text-sm flex-1">{pName}</span>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={item.quantity}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((i) =>
                          i.product_id === item.product_id
                            ? { ...i, quantity: parseInt(e.target.value, 10) || 1 }
                            : i,
                        ),
                      )
                    }
                    className="w-14 rounded border border-gray-300 px-2 py-1 text-sm text-center"
                  />
                  <button
                    onClick={() => removeItem(item.product_id)}
                    className="text-gray-400 hover:text-red-500 text-xs"
                  >
                    ✕
                  </button>
                </div>
              )
            })}
          </div>

          {availableProducts.length > 0 && (
            <div className="flex gap-2">
              <select
                className="flex-1 rounded-lg border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onChange={(e) => {
                  if (e.target.value) addItem(e.target.value)
                  e.target.value = ''
                }}
                defaultValue=""
              >
                <option value="" disabled>
                  {t('admin.combos.add_product')}
                </option>
                {availableProducts.map((p) => {
                  const pName = productNameFor(p, lang)
                  return (
                    <option key={p.id} value={p.id}>
                      {pName}
                    </option>
                  )
                })}
              </select>
            </div>
          )}
          {items.length < 2 && (
            <p className="text-xs text-red-500 mt-1">{t('admin.combos.items_min_hint')}</p>
          )}
        </div>
      </div>
    </Modal>
  )
}
